import { useEffect, useState, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { api, type ForoTema, type ForoPost } from "../lib/api";
import { useAuth } from "../hooks/useAuth";
import { PrivateLayout } from "../components/PrivateLayout";
import { CategoryChip } from "../components/CategoryChip";
import { relTime } from "../lib/time";

const AV_COLORS = [
  "linear-gradient(135deg,#C99A6E,#E2D2BB)", "linear-gradient(135deg,#9CB89F,#C2D6C5)",
  "linear-gradient(135deg,#A88BB0,#D9C6DD)", "linear-gradient(135deg,#8FA8A0,#C2D6C5)",
  "linear-gradient(135deg,#7E96A8,#C2D6C5)", "linear-gradient(135deg,#C9A86B,#E2D2BB)",
];

function PostAvatar({
  nombre, fotoUrl, size = 40,
}: { nombre: string; fotoUrl?: string | null; size?: number }) {
  if (fotoUrl) {
    return (
      <img src={fotoUrl} alt={nombre} style={{
        width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0,
      }} />
    );
  }
  const idx = nombre.charCodeAt(0) % AV_COLORS.length;
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", background: AV_COLORS[idx],
      flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
      color: "#fff", fontWeight: 700, fontSize: size * 0.4, fontFamily: "var(--font-display)",
    }}>
      {nombre[0]?.toUpperCase()}
    </div>
  );
}

function ReplyBox({
  temaId, parentId, onPublished, onCancel,
}: {
  temaId: string;
  parentId?: string;
  onPublished: (post: ForoPost) => void;
  onCancel?: () => void;
}) {
  const { token, user, isAuthenticated } = useAuth();
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isAuthenticated) {
    return (
      <div style={{
        background: "linear-gradient(135deg, var(--sage-soft), var(--sand-3))", borderRadius: 14,
        padding: "1.5rem", textAlign: "center", border: "1px solid rgba(184,212,200,0.4)",
      }}>
        <p style={{ color: "var(--muted)", marginBottom: "1rem", fontSize: "0.9rem" }}>
          Regístrate para participar en la conversación
        </p>
        <Link to="/registro" className="btn btn-primary">Registrarme gratis →</Link>
      </div>
    );
  }

  async function submit() {
    if (!text.trim() || !token) return;
    setLoading(true);
    setError(null);
    console.log("Intentando publicar:", { contenido: text.trim(), tema_id: temaId, user });
    const r = await api.crearPost(
      { tema_id: temaId, contenido: text.trim(), parent_id: parentId },
      token
    );
    setLoading(false);
    if (r.ok) {
      setText("");
      onPublished(r.data.post);
    } else {
      console.error("Error al publicar:", r.error);
      setError(
        r.error.code === "UNAUTHORIZED"
          ? "Tu sesión expiró. Inicia sesión de nuevo para publicar."
          : r.error.message || "No se pudo publicar. Intenta de nuevo."
      );
    }
  }

  return (
    <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
      {user && <PostAvatar nombre={user.nombre} fotoUrl={user.foto_url ?? user.avatar_url} />}
      <div style={{ flex: 1 }}>
        <textarea
          className="form-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={parentId ? "Escribe tu respuesta…" : "Comparte algo amable o cuenta tu experiencia…"}
          rows={parentId ? 2 : 3}
        />
        {error && (
          <p style={{ color: "#b3473a", fontSize: "0.825rem", marginTop: "0.5rem", marginBottom: 0 }}>
            {error}
          </p>
        )}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 12, flexWrap: "wrap", gap: 10 }}>
          <span style={{ fontSize: 13, color: "var(--muted-3)" }}>Sé respetuoso. Este es un espacio seguro 💛</span>
          <div style={{ display: "flex", gap: "0.625rem" }}>
            {onCancel && (
              <button onClick={onCancel} className="btn btn-outline btn--sm">Cancelar</button>
            )}
            <button onClick={submit} disabled={!text.trim() || loading} className="btn btn-primary btn--sm">
              {loading ? <><span className="spinner" /> Publicando…</> : "Responder"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PostItem({
  post, temaId, depth, onLike, onReplyAdded,
}: {
  post: ForoPost;
  temaId: string;
  depth: number;
  onLike: (postId: string) => void;
  onReplyAdded: (parentId: string, newPost: ForoPost) => void;
}) {
  const { token, isAuthenticated } = useAuth();
  const [showReply, setShowReply] = useState(false);
  const [liking, setLiking] = useState(false);

  async function handleLike() {
    if (!token || liking) return;
    setLiking(true);
    onLike(post.id);
    await api.toggleLike(post.id, token);
    setLiking(false);
  }

  return (
    <div style={{ marginLeft: depth > 0 ? 44 : 0, marginTop: depth > 0 ? 14 : 0,
      paddingLeft: depth > 0 ? 18 : 0, borderLeft: depth > 0 ? "2px solid #EFE3D4" : "none" }}>
      <div style={{ display: "flex", gap: "0.875rem" }}>
        <PostAvatar nombre={post.nombre_usuario} fotoUrl={post.foto_url ?? post.avatar_url} size={depth > 0 ? 34 : 40} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 3 }}>
            {post.nombre_usuario} <span style={{ fontSize: 12, color: "var(--muted-3)", fontWeight: 500 }}>· {relTime(post.created_at)}</span>
          </div>
          <p style={{ fontSize: "0.95rem", lineHeight: 1.6, color: "var(--ink-3)", margin: "0 0 10px", whiteSpace: "pre-wrap" }}>
            {post.contenido}
          </p>

          <div style={{ display: "flex", gap: 18, alignItems: "center", color: "var(--muted-2)", fontSize: 13, fontWeight: 600 }}>
            <button onClick={handleLike} disabled={!isAuthenticated}
              style={{ border: "none", background: "none", cursor: isAuthenticated ? "pointer" : "default", padding: 0,
                color: post.liked_by_user ? "var(--copper)" : "var(--muted-2)", fontWeight: 600, fontSize: 13 }}>
              {post.liked_by_user ? "♥" : "♡"} {post.likes}
            </button>
            {depth < 2 && isAuthenticated && (
              <button onClick={() => setShowReply((v) => !v)}
                style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: "var(--muted-2)", fontWeight: 600, fontSize: 13 }}>
                Responder
              </button>
            )}
          </div>

          {showReply && (
            <div style={{ marginTop: "0.875rem" }}>
              <ReplyBox temaId={temaId} parentId={post.id}
                onCancel={() => setShowReply(false)}
                onPublished={(newPost) => {
                  setShowReply(false);
                  onReplyAdded(post.id, newPost);
                }} />
            </div>
          )}

          {post.replies?.map((reply) => (
            <PostItem key={reply.id} post={reply} temaId={temaId}
              depth={depth + 1} onLike={onLike} onReplyAdded={onReplyAdded} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ForoTema() {
  const { tema_id = "" } = useParams<{ tema_id: string }>();
  const { token } = useAuth();

  const [tema,    setTema]    = useState<ForoTema | null>(null);
  const [posts,   setPosts]   = useState<ForoPost[]>([]);
  const [loading, setLoad]    = useState(true);
  const postsEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLoad(true);
    Promise.all([
      api.getForoTema(tema_id),
      api.getForoPosts(tema_id, token ?? undefined),
    ]).then(([temaR, postsR]) => {
      if (temaR.ok) setTema(temaR.data.tema);
      if (postsR.ok) setPosts(postsR.data.posts);
    }).catch(() => {}).finally(() => setLoad(false));
  }, [tema_id]);

  function cargarPosts() {
    api.getForoPosts(tema_id, token ?? undefined).then((postsR) => {
      if (postsR.ok) setPosts(postsR.data.posts);
    }).catch(() => {});
  }

  useEffect(() => {
    const interval = setInterval(() => {
      cargarPosts();
    }, 30000); // 30 segundos

    return () => clearInterval(interval);
  }, [tema_id]);

  function handleLike(postId: string) {
    setPosts((prev) => prev.map((p) => {
      if (p.id === postId) {
        return { ...p, liked_by_user: !p.liked_by_user, likes: p.liked_by_user ? p.likes - 1 : p.likes + 1 };
      }
      return { ...p, replies: p.replies.map((r) =>
        r.id === postId ? { ...r, liked_by_user: !r.liked_by_user, likes: r.liked_by_user ? r.likes - 1 : r.likes + 1 } : r
      )};
    }));
  }

  function handleReplyAdded(parentId: string, newPost: ForoPost) {
    setPosts((prev) => prev.map((p) =>
      p.id === parentId
        ? { ...p, replies: [...(p.replies ?? []), { ...newPost, replies: [] }] }
        : p
    ));
  }

  function handleRootPost(post: ForoPost) {
    setPosts((prev) => [...prev, { ...post, replies: [] }]);
    setTimeout(() => postsEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    cargarPosts();
  }

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: "var(--bg)", display: "grid", placeItems: "center" }}>
        <span className="spinner spinner--dark" />
      </div>
    );
  }

  const totalRespuestas = posts.reduce((sum, p) => sum + 1 + (p.replies?.length ?? 0), 0);

  return (
    <PrivateLayout maxWidth={820}>
      <Link to="/comunidad" style={{ fontSize: 14, color: "var(--copper)", fontWeight: 600, textDecoration: "none", display: "inline-block", marginBottom: 20 }}>
        ‹ Volver a la comunidad
      </Link>

      {/* topic header */}
      <div className="card" style={{ padding: 28, marginBottom: 26 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
          <PostAvatar
            nombre={tema?.creador_nombre ?? "Equipo PzicoDosis"}
            fotoUrl={tema?.creador_foto_url ?? tema?.creador_avatar_url}
            size={46}
          />
          <div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{tema?.creador_nombre ?? "Equipo PzicoDosis"}</div>
            <div style={{ fontSize: 13, color: "var(--muted-3)" }}>{tema && relTime(tema.created_at)}</div>
          </div>
          {tema && <span style={{ marginLeft: "auto" }}><CategoryChip categoria={tema.categoria} /></span>}
        </div>
        <h1 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 28, lineHeight: 1.12, letterSpacing: "-0.02em", margin: "0 0 14px" }}>
          {tema?.titulo ?? "Tema"}
        </h1>
        {tema?.descripcion && (
          <p style={{ fontSize: 16, color: "var(--ink-3)", lineHeight: 1.65, margin: "0 0 20px" }}>
            {tema.descripcion}
          </p>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 12, paddingTop: 18, borderTop: "1px solid #F0E6D6", flexWrap: "wrap" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "var(--sand-3)", color: "var(--copper)", borderRadius: 999, padding: "9px 16px", fontSize: 14, fontWeight: 700 }}>
            💬 {totalRespuestas} respuesta{totalRespuestas !== 1 ? "s" : ""}
          </span>
        </div>
      </div>

      {/* reply composer */}
      <div className="card" style={{ padding: 18, marginBottom: 30 }}>
        <ReplyBox temaId={tema_id} onPublished={handleRootPost} />
      </div>

      {/* replies */}
      <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 18, letterSpacing: "-0.02em", marginBottom: 18 }}>
        {totalRespuestas} respuesta{totalRespuestas !== 1 ? "s" : ""}
      </div>

      {posts.length === 0 ? (
        <div style={{ textAlign: "center", padding: "2rem", color: "var(--muted-2)" }}>
          <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>💬</div>
          <p style={{ fontSize: "0.9rem" }}>Sé el primero en participar.</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
          {posts.map((post) => (
            <PostItem key={post.id} post={post} temaId={tema_id}
              depth={0} onLike={handleLike} onReplyAdded={handleReplyAdded} />
          ))}
        </div>
      )}

      <div ref={postsEndRef} />
    </PrivateLayout>
  );
}
